import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { supabase } from '../services/supabase';
import RastreamentoSolicitacao from './RastreamentoSolicitacao';

interface FornecedorOcorrencias {
  fornecedor: string;
  quantidade: number;
  motivos: string[]; // Array de motivos únicos
  ocorrencias: Array<{
    data: string;
    motivo: string;
    observacao?: string; // Campo adicional para observação
  }>;
}

interface CargaPorDia {
  data: string;
  quantidade: number;
}

interface Indicador {
  titulo: string;
  valor: string | number;
  variacao: number;
  cor: string;
}

const BIAnalitico: React.FC = () => {
  const { filialAtual } = useApp();
  const [abaAtiva, setAbaAtiva] = useState<'dashboard' | 'rastreamento'>('dashboard');
  const [loading, setLoading] = useState(true);
  const [fornecedoresOcorrencias, setFornecedoresOcorrencias] = useState<FornecedorOcorrencias[]>([]);
  const [cargasPorDia, setCargasPorDia] = useState<CargaPorDia[]>([]);
  const [indicadores, setIndicadores] = useState<Indicador[]>([]);
  const [motivosOcorrencias, setMotivosOcorrencias] = useState<string[]>([]);
  const [motivoSelecionado, setMotivoSelecionado] = useState<string>('');
  const [periodoInicio, setPeriodoInicio] = useState<string>('');
  const [periodoFim, setPeriodoFim] = useState<string>('');
  const [buscaFornecedor, setBuscaFornecedor] = useState<string>('');
  const [buscaMotivo, setBuscaMotivo] = useState<string>('');

  // Carregar dados iniciais
  useEffect(() => {
    if (abaAtiva === 'dashboard') {
      carregarDadosBI();
    }
  }, [filialAtual, abaAtiva]);

  // Recarregar dados quando os filtros de busca mudam
  useEffect(() => {
    if (abaAtiva === 'dashboard' && periodoInicio && periodoFim) {
      carregarCargasPorDia(periodoInicio, periodoFim, buscaFornecedor, buscaMotivo);
      carregarIndicadores(buscaFornecedor, buscaMotivo);
    }
  }, [periodoInicio, periodoFim, buscaFornecedor, buscaMotivo, abaAtiva]);

  const carregarDadosBI = async () => {
    try {
      setLoading(true);
      
      // Definir período padrão (últimos 30 dias)
      const hoje = new Date();
      const trintaDiasAtras = new Date();
      trintaDiasAtras.setDate(hoje.getDate() - 30);
      
      setPeriodoInicio(trintaDiasAtras.toISOString().split('T')[0]);
      setPeriodoFim(hoje.toISOString().split('T')[0]);

      await Promise.all([
        carregarFornecedoresOcorrencias(trintaDiasAtras.toISOString().split('T')[0], hoje.toISOString().split('T')[0]),
        carregarCargasPorDia(trintaDiasAtras.toISOString().split('T')[0], hoje.toISOString().split('T')[0]),
        carregarIndicadores(),
        carregarMotivosOcorrencias()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados do BI:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarFornecedoresOcorrencias = async (dataInicio: string, dataFim: string) => {
    try {
      let query = supabase
        .from('cargas')
        .select(`
          id,
          fornecedor,
          ocorrencia,
          created_at
        `)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      // Filtrar por filial se selecionada
      if (filialAtual) {
        query = query.eq('filial', filialAtual);
      }

      const { data: cargas, error } = await query;

      if (error) throw error;

      // Agrupar ocorrências por fornecedor
      const fornecedoresMap = new Map<string, FornecedorOcorrencias>();

      cargas?.forEach(carga => {
        if (carga.ocorrencia) {
          // Verificar se é string (formato novo) ou JSON (formato antigo)
          if (typeof carga.ocorrencia === 'string') {
            // Verificar se é string JSON ou string simples
            let ocorrenciasProcessadas: any[] = [];
            
            try {
              // Tentar parsear como JSON
              const parseado = JSON.parse(carga.ocorrencia);
              
              if (Array.isArray(parseado)) {
                // Formato antigo: array de objetos
                ocorrenciasProcessadas = parseado;
              } else if (parseado.motivos) {
                // Formato intermediário: objeto com motivos
                ocorrenciasProcessadas = parseado.motivos.map((motivo: string) => ({
                  motivo: motivo,
                  observacao: parseado.observacao || '',
                  data: parseado.data || carga.created_at
                }));
              } else {
                // Objeto único, tratar como string simples
                throw new Error('Não é formato esperado');
              }
            } catch {
              // String simples (formato novo): "Motivo | Obs: observação"
              const partes = carga.ocorrencia.split(' | Obs: ');
              const motivo = partes[0].trim();
              const observacao = partes.length > 1 ? partes[1].trim() : '';
              
              ocorrenciasProcessadas = [{
                motivo: motivo,
                observacao: observacao,
                data: carga.created_at
              }];
            }
            
            // Processar ocorrências
            const fornecedor = carga.fornecedor || 'Não informado';
            
            if (!fornecedoresMap.has(fornecedor)) {
              fornecedoresMap.set(fornecedor, {
                fornecedor,
                quantidade: 0,
                motivos: [],
                ocorrencias: []
              });
            }

            const fornecedorData = fornecedoresMap.get(fornecedor)!;
            
            ocorrenciasProcessadas.forEach(ocorrencia => {
              fornecedorData.quantidade++;
              
              // Adicionar motivo se ainda não existe
              if (!fornecedorData.motivos.includes(ocorrencia.motivo)) {
                fornecedorData.motivos.push(ocorrencia.motivo);
              }
              
              // Adicionar ocorrência completa
              fornecedorData.ocorrencias.push({
                data: ocorrencia.data,
                motivo: ocorrencia.motivo,
                observacao: ocorrencia.observacao
              });
            });
          } else {
            // Se for objeto/array direto (formato antigo)
            const ocorrenciasArray = Array.isArray(carga.ocorrencia) ? carga.ocorrencia : [carga.ocorrencia];
            
            ocorrenciasArray.forEach((ocorrencia: any) => {
              const fornecedor = carga.fornecedor || 'Não informado';
              
              if (!fornecedoresMap.has(fornecedor)) {
                fornecedoresMap.set(fornecedor, {
                  fornecedor,
                  quantidade: 0,
                  motivos: [],
                  ocorrencias: []
                });
              }

              const fornecedorData = fornecedoresMap.get(fornecedor)!;
              fornecedorData.quantidade++;
              
              // Adicionar motivo se ainda não existir
              const motivo = ocorrencia.motivo || ocorrencia.descricao || 'Não informado';
              if (!fornecedorData.motivos.includes(motivo)) {
                fornecedorData.motivos.push(motivo);
              }
              
              fornecedorData.ocorrencias.push({
                data: ocorrencia.data || carga.created_at,
                motivo: motivo,
                observacao: ocorrencia.observacao || ocorrencia.observacoes || ''
              });
            });
          }
        }
      });

      // Converter para array e ordenar por quantidade
      const fornecedoresArray = Array.from(fornecedoresMap.values())
        .sort((a, b) => b.quantidade - a.quantidade);

      setFornecedoresOcorrencias(fornecedoresArray);
    } catch (error) {
      console.error('Erro ao carregar fornecedores com ocorrências:', error);
    }
  };

  const carregarCargasPorDia = async (dataInicio: string, dataFim: string, filtroFornecedor?: string, filtroMotivo?: string) => {
    try {
      let query = supabase
        .from('cargas')
        .select('created_at, fornecedor, ocorrencia')
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      // Filtrar por filial se selecionada
      if (filialAtual) {
        query = query.eq('filial', filialAtual);
      }

      const { data: cargas, error } = await query;

      if (error) throw error;

      // Filtrar cargas baseado nos critérios de busca
      const cargasFiltradas = cargas?.filter(carga => {
        // Filtro por fornecedor
        if (filtroFornecedor && !carga.fornecedor?.toLowerCase().includes(filtroFornecedor.toLowerCase())) {
          return false;
        }
        
        // Filtro por motivo da ocorrência
        if (filtroMotivo && carga.ocorrencia) {
          let motivoEncontrado = false;
          
          if (typeof carga.ocorrencia === 'string') {
            motivoEncontrado = carga.ocorrencia.toLowerCase().includes(filtroMotivo.toLowerCase());
          } else {
            const ocorrenciasArray = Array.isArray(carga.ocorrencia) ? carga.ocorrencia : [carga.ocorrencia];
            motivoEncontrado = ocorrenciasArray.some((ocorrencia: any) => 
              (ocorrencia.motivo && ocorrencia.motivo.toLowerCase().includes(filtroMotivo.toLowerCase())) ||
              (ocorrencia.descricao && ocorrencia.descricao.toLowerCase().includes(filtroMotivo.toLowerCase()))
            );
          }
          
          if (!motivoEncontrado) {
            return false;
          }
        }
        
        return true;
      }) || [];

      // Agrupar cargas por dia
      const cargasPorDiaMap = new Map<string, number>();

      cargasFiltradas.forEach(carga => {
        const data = carga.created_at?.split('T')[0];
        if (data) {
          cargasPorDiaMap.set(data, (cargasPorDiaMap.get(data) || 0) + 1);
        }
      });

      // Converter para array e ordenar por data
      const cargasArray = Array.from(cargasPorDiaMap.entries())
        .map(([data, quantidade]) => ({ data, quantidade }))
        .sort((a, b) => a.data.localeCompare(b.data));

      setCargasPorDia(cargasArray);
    } catch (error) {
      console.error('Erro ao carregar cargas por dia:', error);
    }
  };

  const carregarIndicadores = async (filtroFornecedor?: string, filtroMotivo?: string) => {
    try {
      let query = supabase
        .from('cargas')
        .select('*');

      // Filtrar por filial se selecionada
      if (filialAtual) {
        query = query.eq('filial', filialAtual);
      }

      const { data: cargas, error } = await query;

      if (error) throw error;

      // Filtrar cargas baseado nos critérios de busca
      const cargasFiltradas = cargas?.filter(carga => {
        // Filtro por fornecedor
        if (filtroFornecedor && !carga.fornecedor?.toLowerCase().includes(filtroFornecedor.toLowerCase())) {
          return false;
        }
        
        // Filtro por motivo da ocorrência
        if (filtroMotivo && carga.ocorrencia) {
          let motivoEncontrado = false;
          
          if (typeof carga.ocorrencia === 'string') {
            motivoEncontrado = carga.ocorrencia.toLowerCase().includes(filtroMotivo.toLowerCase());
          } else {
            const ocorrenciasArray = Array.isArray(carga.ocorrencia) ? carga.ocorrencia : [carga.ocorrencia];
            motivoEncontrado = ocorrenciasArray.some((ocorrencia: any) => 
              (ocorrencia.motivo && ocorrencia.motivo.toLowerCase().includes(filtroMotivo.toLowerCase())) ||
              (ocorrencia.descricao && ocorrencia.descricao.toLowerCase().includes(filtroMotivo.toLowerCase()))
            );
          }
          
          if (!motivoEncontrado) {
            return false;
          }
        }
        
        return true;
      }) || [];

      const totalCargas = cargasFiltradas.length;
      const cargasComSAgendamento = cargasFiltradas.filter(c => {
        if (!c.ocorrencia) return false;
        
        if (typeof c.ocorrencia === 'string') {
          return c.ocorrencia.toLowerCase().includes('s/ agendamento');
        } else {
          const ocorrenciasArray = Array.isArray(c.ocorrencia) ? c.ocorrencia : [c.ocorrencia];
          return ocorrenciasArray.some((ocorrencia: any) => 
            (ocorrencia.motivo && ocorrencia.motivo.toLowerCase().includes('s/ agendamento')) ||
            (ocorrencia.descricao && ocorrencia.descricao.toLowerCase().includes('s/ agendamento'))
          );
        }
      }).length || 0;
      const taxaOcorrencias = totalCargas > 0 ? (cargasComSAgendamento / totalCargas) * 100 : 0;

      // Calcular média diária contando apenas dias com cargas registradas
      let mediaDiaria = 0;
      
      // Agrupar cargas por data para contar dias com registros
      const diasComCargas = new Set<string>();
      cargasFiltradas.forEach(carga => {
        if (carga.created_at) {
          const data = new Date(carga.created_at).toLocaleDateString('pt-BR');
          diasComCargas.add(data);
        }
      });
      
      const numeroDiasComCargas = diasComCargas.size;
      mediaDiaria = numeroDiasComCargas > 0 ? totalCargas / numeroDiasComCargas : 0;

      const novosIndicadores: Indicador[] = [
        {
          titulo: 'Total de Cargas',
          valor: totalCargas,
          variacao: 0,
          cor: 'text-blue-600'
        },
        {
          titulo: 'Taxa de Ocorrências',
          valor: `${taxaOcorrencias.toFixed(1)}%`,
          variacao: 0,
          cor: 'text-yellow-600'
        },
        {
          titulo: 'Média Diária de Cargas',
          valor: mediaDiaria.toFixed(1),
          variacao: 0,
          cor: 'text-green-600'
        },
        {
          titulo: 'Cargas Sem Agendamento',
          valor: cargasComSAgendamento,
          variacao: 0,
          cor: 'text-red-600'
        }
      ];

      setIndicadores(novosIndicadores);
    } catch (error) {
      console.error('Erro ao carregar indicadores:', error);
    }
  };

  const carregarMotivosOcorrencias = async () => {
    try {
      let query = supabase
        .from('cargas')
        .select('ocorrencia');

      // Filtrar por filial se selecionada
      if (filialAtual) {
        query = query.eq('filial', filialAtual);
      }

      const { data: cargas, error } = await query;

      if (error) throw error;

      const motivosSet = new Set<string>();

      cargas?.forEach(carga => {
        if (carga.ocorrencia) {
          // Se for um objeto, tratar como array de um elemento
          const ocorrenciasArray = Array.isArray(carga.ocorrencia) ? carga.ocorrencia : [carga.ocorrencia];
          
          ocorrenciasArray.forEach((ocorrencia: any) => {
            if (ocorrencia.motivo) {
              motivosSet.add(ocorrencia.motivo);
            }
          });
        }
      });

      const motivosArray = Array.from(motivosSet).sort();
      setMotivosOcorrencias(motivosArray);
    } catch (error) {
      console.error('Erro ao carregar motivos de ocorrências:', error);
    }
  };

  const handleFiltrar = () => {
    if (periodoInicio && periodoFim) {
      carregarFornecedoresOcorrencias(periodoInicio, periodoFim);
      carregarCargasPorDia(periodoInicio, periodoFim, buscaFornecedor, buscaMotivo);
      carregarIndicadores(buscaFornecedor, buscaMotivo);
    }
  };

  const handleMotivoChange = (motivo: string) => {
    setMotivoSelecionado(motivo);
    if (periodoInicio && periodoFim) {
      carregarFornecedoresOcorrencias(periodoInicio, periodoFim);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Abas */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setAbaAtiva('dashboard')}
            className={`${
              abaAtiva === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            📊 Dashboard Geral
          </button>
          <button
            onClick={() => setAbaAtiva('rastreamento')}
            className={`${
              abaAtiva === 'rastreamento'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            🔍 Rastreamento de Solicitações
          </button>
        </nav>
      </div>

      {/* Conteúdo das Abas */}
      {abaAtiva === 'dashboard' ? (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Geral</h1>
            <div className="flex gap-4">
              <input
                type="date"
                value={periodoInicio}
                onChange={(e) => setPeriodoInicio(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <input
                type="date"
                value={periodoFim}
                onChange={(e) => setPeriodoFim(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
          <button
            onClick={handleFiltrar}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Filtrar
          </button>
        </div>
      </div>

      {/* Campos de busca */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Busca Rápida</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar por Fornecedor
            </label>
            <input
              type="text"
              value={buscaFornecedor}
              onChange={(e) => setBuscaFornecedor(e.target.value)}
              placeholder="Digite o nome do fornecedor..."
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar por Motivo da Ocorrência
            </label>
            <input
              type="text"
              value={buscaMotivo}
              onChange={(e) => setBuscaMotivo(e.target.value)}
              placeholder="Digite o motivo da ocorrência..."
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => {
              setBuscaFornecedor('');
              setBuscaMotivo('');
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Limpar Busca
          </button>
        </div>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {indicadores.map((indicador, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">{indicador.titulo}</h3>
            <p className={`text-2xl font-bold mt-2 ${indicador.cor}`}>{indicador.valor}</p>
            {indicador.variacao !== 0 && (
              <p className={`text-sm mt-2 ${indicador.variacao > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {indicador.variacao > 0 ? '+' : ''}{indicador.variacao}% vs período anterior
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fornecedores com mais ocorrências */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Fornecedores com Mais Ocorrências</h2>
            <select
              value={motivoSelecionado}
              onChange={(e) => handleMotivoChange(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Todos os motivos</option>
              {motivosOcorrencias.map(motivo => (
                <option key={motivo} value={motivo}>{motivo}</option>
              ))}
            </select>
          </div>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {fornecedoresOcorrencias.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhuma ocorrência encontrada</p>
            ) : (
              fornecedoresOcorrencias
                .filter(f => {
                  // Filtro por motivo selecionado no select
                  const filtroMotivoSelect = !motivoSelecionado || f.motivos.includes(motivoSelecionado);
                  
                  // Filtro por busca de fornecedor
                  const filtroFornecedor = !buscaFornecedor || 
                    f.fornecedor.toLowerCase().includes(buscaFornecedor.toLowerCase());
                  
                  // Filtro por busca de motivo
                  const filtroMotivoBusca = !buscaMotivo || 
                    f.motivos.some(m => m.toLowerCase().includes(buscaMotivo.toLowerCase()));
                  
                  return filtroMotivoSelect && filtroFornecedor && filtroMotivoBusca;
                })
                .slice(0, 10)
                .map((fornecedor, index) => (
                  <div key={index} className="border-l-4 border-red-500 pl-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">{fornecedor.fornecedor}</h3>
                        <p className="text-sm text-gray-500">{fornecedor.quantidade} ocorrências</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {fornecedor.motivos.map((motivo, idx) => (
                            <span key={idx} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                              {motivo}
                            </span>
                          ))}
                        </div>
                      </div>
                      <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        #{index + 1}
                      </span>
                    </div>
                    <div className="mt-3 space-y-2">
                      {fornecedor.ocorrencias
                        .filter(o => {
                          // Filtro por motivo selecionado no select
                          const filtroMotivoSelect = !motivoSelecionado || o.motivo === motivoSelecionado;
                          
                          // Filtro por busca de motivo
                          const filtroMotivoBusca = !buscaMotivo || 
                            o.motivo.toLowerCase().includes(buscaMotivo.toLowerCase());
                          
                          return filtroMotivoSelect && filtroMotivoBusca;
                        })
                        .slice(0, 5)
                        .map((ocorrencia, idx) => (
                          <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-medium text-sm text-gray-900 bg-blue-100 px-2 py-1 rounded">
                                {ocorrencia.motivo}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(ocorrencia.data).toLocaleDateString('pt-BR')}
                              </span>
                            </div>
                                                        {ocorrencia.observacao && (
                              <p className="text-sm text-gray-600 italic">
                                <span className="font-medium">Observação:</span> {ocorrencia.observacao}
                              </p>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>

        {/* Cargas por dia */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Cargas Recebidas por Dia</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {cargasPorDia.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhuma carga encontrada</p>
            ) : (
              cargasPorDia.map((carga, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium text-gray-900">
                      {new Date(carga.data).toLocaleDateString('pt-BR', { 
                        weekday: 'short', 
                        day: 'numeric', 
                        month: 'short' 
                      })}
                    </p>
                    <p className="text-sm text-gray-500">{carga.data}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary-600">{carga.quantidade}</p>
                    <p className="text-xs text-gray-500">cargas</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      </div>
      ) : (
        <RastreamentoSolicitacao />
      )}
    </div>
  );
};

export default BIAnalitico;
